/**
 * Hook para gerenciamento de etiquetas (labels) do módulo de inbox
 * 
 * Funcionalidades:
 * - Listar etiquetas de uma inbox
 * - Criar, atualizar e deletar etiquetas
 * - Atribuir/remover etiquetas de conversas
 * - Sincronização com provider WAHA
 * 
 * Arquitetura multi-provider: utiliza provider-proxy para operações
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger condicional para o módulo
const labelsLogger = createLogger('useLabels');

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

/**
 * Interface de uma etiqueta (label)
 * Corresponde ao schema da tabela inbox_labels
 */
export interface Label {
  id: string;
  inbox_id: string;
  tenant_id: string;
  provider_label_id: string | null;
  name: string;
  color_index: number | null;
  color_hex: string | null;
  source: string;
  is_synced_to_provider: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Interface para criar uma nova etiqueta
 */
export interface CreateLabelInput {
  name: string;
  color?: number; // Índice de cor do provider (0-19 para WAHA) - DEVE ser número
  color_hex?: string; // Cor hexadecimal para exibição
}

/**
 * Interface para atualizar uma etiqueta
 */
export interface UpdateLabelInput {
  name?: string;
  color?: number; // Índice de cor do provider - DEVE ser número
  color_hex?: string;
}

/**
 * Interface de etiqueta atribuída a uma conversa
 */
export interface ConversationLabel {
  label_id: string;
  conversation_id: string;
  assigned_at: string;
  assigned_by: string | null;
  source: string;
  is_synced_to_provider: boolean;
  label: Label;
}

// =============================================================================
// CORES PADRÃO DO WAHA/WHATSAPP - 20 cores oficiais
// =============================================================================

/**
 * Mapeamento de cores do WhatsApp/WAHA (números para hex)
 * Lista oficial das 20 cores disponíveis na API WAHA
 */
export const WHATSAPP_LABEL_COLORS: Record<string, string> = {
  '0': '#ff9485',   // Coral
  '1': '#64c4ff',   // Azul claro
  '2': '#ffd429',   // Amarelo
  '3': '#dfaef0',   // Lilás
  '4': '#99b6c1',   // Cinza azulado
  '5': '#55ccb3',   // Verde água
  '6': '#ff9dff',   // Rosa
  '7': '#d3a91d',   // Dourado
  '8': '#6d7cce',   // Roxo azulado
  '9': '#d7e752',   // Verde limão
  '10': '#00d0e2',  // Ciano
  '11': '#ffc5c7',  // Rosa claro
  '12': '#93ceac',  // Verde menta
  '13': '#f74848',  // Vermelho
  '14': '#00a0f2',  // Azul
  '15': '#83e422',  // Verde
  '16': '#ffaf04',  // Laranja
  '17': '#b5ebff',  // Azul bebê
  '18': '#9ba6ff',  // Lavanda
  '19': '#9368cf',  // Roxo
};

/**
 * Retorna o hex de uma cor baseado no código do provider
 */
export const getLabelColorHex = (colorCode: string): string => {
  return WHATSAPP_LABEL_COLORS[colorCode] || WHATSAPP_LABEL_COLORS['0'];
};

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

interface UseLabelsOptions {
  /** ID da inbox para buscar etiquetas */
  inboxId: string | null;
  /** Se deve buscar automaticamente ao montar */
  enabled?: boolean;
}

export function useLabels({ inboxId, enabled = true }: UseLabelsOptions) {
  const queryClient = useQueryClient();
  const [syncingLabels, setSyncingLabels] = useState(false);

  // =========================================================================
  // QUERY: Listar etiquetas da inbox
  // =========================================================================

  const {
    data: labels = [],
    isLoading: loadingLabels,
    error: labelsError,
    refetch: refetchLabels,
  } = useQuery({
    queryKey: ['inbox-labels', inboxId],
    queryFn: async () => {
      if (!inboxId) return [];

      labelsLogger.debug('Buscando etiquetas da inbox', { inboxId });

      const { data, error } = await supabase
        .from('inbox_labels')
        .select('*')
        .eq('inbox_id', inboxId)
        .order('name', { ascending: true });

      if (error) {
        labelsLogger.error('Erro ao buscar etiquetas', error);
        throw error;
      }

      return data as Label[];
    },
    enabled: enabled && !!inboxId,
    staleTime: 30000, // Cache por 30 segundos
  });

  // =========================================================================
  // MUTATION: Criar etiqueta
  // =========================================================================

  const createLabelMutation = useMutation({
    mutationFn: async (input: CreateLabelInput) => {
      if (!inboxId) throw new Error('inboxId é obrigatório');

      labelsLogger.info('Criando etiqueta', { inboxId, input });

      // Garante que color é sempre número para compatibilidade com WAHA API
      const colorNumber = typeof input.color === 'number' ? input.color : parseInt(String(input.color || 0), 10);
      
      // Chama provider-proxy para criar no provider e no banco
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'create_label',
          label_name: input.name,
          label_color: colorNumber,  // Número, não string
          label_color_hex: input.color_hex || getLabelColorHex(String(colorNumber)),
        },
      });

      if (error) {
        labelsLogger.error('Erro ao criar etiqueta', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao criar etiqueta');
      }

      return data.label as Label;
    },
    onSuccess: (newLabel) => {
      // Guarda contra resposta undefined/null
      if (newLabel?.id) {
        labelsLogger.info('Etiqueta criada com sucesso', { labelId: newLabel.id });
      } else {
        labelsLogger.warn('Etiqueta criada mas sem ID retornado, refetching...');
      }
      queryClient.invalidateQueries({ queryKey: ['inbox-labels', inboxId] });
      toast.success('Etiqueta criada com sucesso');
    },
    onError: (error) => {
      labelsLogger.error('Falha ao criar etiqueta', error);
      toast.error('Erro ao criar etiqueta');
    },
  });

  // =========================================================================
  // MUTATION: Atualizar etiqueta
  // =========================================================================

  const updateLabelMutation = useMutation({
    mutationFn: async ({ labelId, input }: { labelId: string; input: UpdateLabelInput }) => {
      if (!inboxId) throw new Error('inboxId é obrigatório');

      labelsLogger.info('Atualizando etiqueta', { labelId, input });

      // Garante que color é sempre número para compatibilidade com WAHA API
      const colorNumber = input.color !== undefined 
        ? (typeof input.color === 'number' ? input.color : parseInt(String(input.color), 10))
        : undefined;
      
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'update_label',
          label_id: labelId,
          label_name: input.name,
          label_color: colorNumber,  // Número ou undefined
          label_color_hex: input.color_hex,
        },
      });

      if (error) {
        labelsLogger.error('Erro ao atualizar etiqueta', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao atualizar etiqueta');
      }

      return data.label as Label;
    },
    onSuccess: (updatedLabel) => {
      // Guarda contra resposta undefined/null
      if (updatedLabel?.id) {
        labelsLogger.info('Etiqueta atualizada', { labelId: updatedLabel.id });
      } else {
        labelsLogger.warn('Etiqueta atualizada mas sem ID retornado');
      }
      queryClient.invalidateQueries({ queryKey: ['inbox-labels', inboxId] });
      toast.success('Etiqueta atualizada');
    },
    onError: (error) => {
      labelsLogger.error('Falha ao atualizar etiqueta', error);
      toast.error('Erro ao atualizar etiqueta');
    },
  });

  // =========================================================================
  // MUTATION: Deletar etiqueta
  // =========================================================================

  const deleteLabelMutation = useMutation({
    mutationFn: async (labelId: string) => {
      if (!inboxId) throw new Error('inboxId é obrigatório');

      labelsLogger.info('Deletando etiqueta', { labelId });

      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'delete_label',
          label_id: labelId,
        },
      });

      if (error) {
        labelsLogger.error('Erro ao deletar etiqueta', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao deletar etiqueta');
      }

      return labelId;
    },
    onSuccess: (deletedId) => {
      labelsLogger.info('Etiqueta deletada', { labelId: deletedId });
      queryClient.invalidateQueries({ queryKey: ['inbox-labels', inboxId] });
      toast.success('Etiqueta removida');
    },
    onError: (error) => {
      labelsLogger.error('Falha ao deletar etiqueta', error);
      toast.error('Erro ao remover etiqueta');
    },
  });

  // =========================================================================
  // MUTATION: Atribuir etiquetas a uma conversa
  // =========================================================================

  const assignLabelsMutation = useMutation({
    mutationFn: async ({ 
      conversationId, 
      labelIds,
      chatId,
    }: { 
      conversationId: string; 
      labelIds: string[];
      chatId: string; // ID externo do chat (phone@c.us ou grupo)
    }) => {
      if (!inboxId) throw new Error('inboxId é obrigatório');

      labelsLogger.info('Atribuindo etiquetas à conversa', { 
        conversationId, 
        labelIds,
        chatId,
      });

      // Chama provider-proxy para sincronizar com o provider
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'set_chat_labels',
          chat_id: chatId,
          label_ids: labelIds,
          conversation_id: conversationId,
        },
      });

      if (error) {
        labelsLogger.error('Erro ao atribuir etiquetas', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao atribuir etiquetas');
      }

      return { conversationId, labelIds };
    },
    onSuccess: ({ conversationId }) => {
      labelsLogger.info('Etiquetas atribuídas com sucesso', { conversationId });
      queryClient.invalidateQueries({ queryKey: ['conversation-labels', conversationId] });
      toast.success('Etiquetas atualizadas');
    },
    onError: (error) => {
      labelsLogger.error('Falha ao atribuir etiquetas', error);
      toast.error('Erro ao atribuir etiquetas');
    },
  });

  // =========================================================================
  // FUNÇÃO: Sincronizar etiquetas do provider
  // =========================================================================

  const syncLabelsFromProvider = useCallback(async () => {
    if (!inboxId) return;

    setSyncingLabels(true);
    labelsLogger.info('Sincronizando etiquetas do provider', { inboxId });

    try {
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'sync_labels',
        },
      });

      if (error) {
        labelsLogger.error('Erro ao sincronizar etiquetas', error);
        toast.error('Erro ao sincronizar etiquetas');
        return;
      }

      if (!data?.success) {
        toast.error(data?.error || 'Erro ao sincronizar etiquetas');
        return;
      }

      labelsLogger.info('Etiquetas sincronizadas', { 
        total: data.labels?.length || 0,
      });
      
      queryClient.invalidateQueries({ queryKey: ['inbox-labels', inboxId] });
      toast.success(`${data.labels?.length || 0} etiquetas sincronizadas`);
    } finally {
      setSyncingLabels(false);
    }
  }, [inboxId, queryClient]);

  // =========================================================================
  // QUERY: Buscar etiquetas de uma conversa específica
  // =========================================================================

  const useConversationLabels = (conversationId: string | null) => {
    return useQuery({
      queryKey: ['conversation-labels', conversationId],
      queryFn: async () => {
        if (!conversationId) return [];

        const { data, error } = await supabase
          .from('conversation_labels')
          .select(`
            label_id,
            conversation_id,
            assigned_at,
            assigned_by,
            source,
            is_synced_to_provider,
            inbox_labels (
              id,
              inbox_id,
              tenant_id,
              provider_label_id,
              name,
              color_index,
              color_hex,
              source,
              is_synced_to_provider,
              last_synced_at,
              sync_error,
              created_at,
              updated_at
            )
          `)
          .eq('conversation_id', conversationId);

        if (error) {
          labelsLogger.error('Erro ao buscar etiquetas da conversa', error);
          throw error;
        }

        // Formata para incluir o label completo
        return (data || []).map((cl) => ({
          label_id: cl.label_id,
          conversation_id: cl.conversation_id,
          assigned_at: cl.assigned_at,
          assigned_by: cl.assigned_by,
          source: cl.source,
          is_synced_to_provider: cl.is_synced_to_provider,
          label: cl.inbox_labels as unknown as Label,
        })) as ConversationLabel[];
      },
      enabled: !!conversationId,
      staleTime: 15000,
    });
  };

  // =========================================================================
  // RETORNO DO HOOK
  // =========================================================================

  return {
    // Estado das etiquetas da inbox
    labels,
    loadingLabels,
    labelsError,
    refetchLabels,

    // Mutations
    createLabel: createLabelMutation.mutateAsync,
    creatingLabel: createLabelMutation.isPending,
    
    updateLabel: (labelId: string, input: UpdateLabelInput) => 
      updateLabelMutation.mutateAsync({ labelId, input }),
    updatingLabel: updateLabelMutation.isPending,
    
    deleteLabel: deleteLabelMutation.mutateAsync,
    deletingLabel: deleteLabelMutation.isPending,
    
    assignLabels: assignLabelsMutation.mutateAsync,
    assigningLabels: assignLabelsMutation.isPending,

    // Sincronização
    syncLabelsFromProvider,
    syncingLabels,

    // Sub-hook para etiquetas de conversa
    useConversationLabels,

    // Utilitários
    getLabelColorHex,
    WHATSAPP_LABEL_COLORS,
  };
}
